import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Book, Music, Video, CheckCircle, ShoppingCart, PlayCircle } from 'lucide-react';
import Footer from './Footer';

const MindsetShiftEcosystemPage = () => {
  const navigate = useNavigate();

  const ecosystemComponents = [
    {
      id: 1,
      title: 'The Mindset Shift',
      subtitle: 'The Book',
      price: 19.99,
      cover: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/pjhwtif3_THE%20MINDSET%20SHIFT%20-%20cover.png',
      available: false,
      type: 'book'
    },
    {
      id: 2,
      title: 'The Mindset Shift',
      subtitle: 'The Workbook',
      price: 24.99,
      cover: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/dmdu0u7i_MINDSET%20SHIFT%20WORKBOOK%20COVER.png',
      available: false,
      type: 'book'
    },
    {
      id: 3,
      title: 'The Mindset Shift',
      subtitle: 'Book of Daily Affirmations',
      price: 14.99,
      cover: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/y4rkhenp_MINDSET%20SHIFT%20BOOK%20OF%20DAILY%20AFFIRMATIONS.png',
      available: false,
      type: 'book'
    },
    {
      id: 4,
      title: 'The Mindset Shift',
      subtitle: 'True Companion Journal',
      price: 17.99,
      cover: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/fap9i5s3_MINDSET%20SHIFT%20TRUE%20COMPANION%20JOURNAL.png',
      available: false,
      type: 'book'
    },
    {
      id: 5,
      title: 'The Official Soundtrack',
      subtitle: '11 Transformational Tracks',
      price: 9.99,
      cover: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png',
      available: true,
      type: 'soundtrack',
      link: '/mindset-shift-soundtrack'
    },
    {
      id: 6,
      title: 'Jersey Mindset Auntie',
      subtitle: 'Video Series',
      price: 'FREE',
      available: true,
      type: 'video',
      link: 'https://youtube.com/shorts/T-RXF5lkF7A?si=rYNc54mNvTpOxHgr'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-sm py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/')}
            className="text-white hover:text-red-500 transition-colors flex items-center gap-2"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4">
              THE <span className="text-red-600">MINDSET SHIFT</span>
              <sup className="text-2xl">®</sup> <span className="text-green-400">eCoSystem</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-300 mb-6">
              A Leadership & Performance Framework for Organizations
            </p>
            
            <div className="max-w-4xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                <span className="text-red-500">Rewire</span> Your Thinking.<br/>
                <span className="text-green-400">Rebuild</span> Your Life.<br/>
                <span className="text-white">Create</span> Your Legacy.
              </h2>
              <p className="text-xl text-gray-300 italic mb-8">
                "A leadership & performance mindset framework"
              </p>
            </div>

            {/* Key Message */}
            <div className="bg-green-900/30 backdrop-blur-md border border-green-500/50 rounded-2xl p-8 max-w-4xl mx-auto mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-green-400 mb-4">
                THE MINDSET SHIFT® eCoSystem
              </h3>
              <p className="text-lg text-gray-200 leading-relaxed">
                addresses the root cause behind burnout, disengagement, and stalled performance: 
                <span className="text-white font-bold"> mindset under pressure</span>.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                This is not motivation.
              </h3>
              <p className="text-lg text-gray-200 leading-relaxed">
                It is a repeatable, practical framework used to support culture, leadership presence, 
                communication, and <span className="italic">ownerthinking</span>.
              </p>
            </div>
          </div>

          {/* eCoSystem Components Grid */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-12">
              The Complete eCoSystem
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {ecosystemComponents.map((component) => (
                <div 
                  key={component.id}
                  className="bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden border border-white/20 hover:border-green-400/50 transition-all hover:scale-105"
                >
                  {/* Cover Image */}
                  {component.cover && (
                    <div className="aspect-[3/4] bg-black/50 relative">
                      <img 
                        src={component.cover}
                        alt={component.title}
                        className="w-full h-full object-contain p-4"
                      />
                      {!component.available && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-yellow-500 text-black px-4 py-2 rounded-full font-bold text-sm">
                            COMING SOON
                          </span>
                        </div>
                      )}
                      {component.available && component.type === 'soundtrack' && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm">
                            AVAILABLE NOW
                          </span>
                        </div>
                      )}
                      {component.type === 'video' && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2">
                            <PlayCircle className="w-4 h-4" />
                            WATCH NOW
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {component.type === 'video' && !component.cover && (
                    <div className="aspect-[3/4] bg-gradient-to-br from-red-600 to-black flex items-center justify-center">
                      <Video className="w-24 h-24 text-white/50" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {component.title}
                    </h3>
                    <p className="text-gray-300 mb-4">
                      {component.subtitle}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-green-400">
                        {typeof component.price === 'number' ? `$${component.price}` : component.price}
                      </span>
                      
                      {component.available ? (
                        component.type === 'soundtrack' ? (
                          <Button 
                            onClick={() => navigate(component.link)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Music className="w-4 h-4 mr-2" />
                            Get Now
                          </Button>
                        ) : component.type === 'video' ? (
                          <a href={component.link} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-red-600 hover:bg-red-700 text-white">
                              <PlayCircle className="w-4 h-4 mr-2" />
                              Watch
                            </Button>
                          </a>
                        ) : null
                      ) : (
                        <Button disabled className="bg-gray-600 cursor-not-allowed text-white">
                          Coming Soon
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bundle Pricing */}
            <div className="bg-gradient-to-br from-green-900/50 to-blue-900/50 backdrop-blur-md border-2 border-green-500 rounded-2xl p-8 max-w-2xl mx-auto text-center">
              <h3 className="text-3xl font-black text-white mb-4">
                Complete eCoSystem Bundle
              </h3>
              <p className="text-gray-200 mb-6">
                All 4 books + Soundtrack + Video Access
              </p>
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className="text-3xl text-gray-400 line-through">$107.96</span>
                <span className="text-5xl font-black text-green-400">$97</span>
              </div>
              <Button 
                disabled
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg px-8 py-6 cursor-not-allowed"
              >
                Pre-Orders Coming Soon
              </Button>
              <p className="text-sm text-gray-400 mt-4">
                Save $10.96 when you get the complete transformation package
              </p>
            </div>
          </div>

          {/* Why Organizations Use This System */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-12 mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-12">
              WHY ORGANIZATIONS USE THIS SYSTEM
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    Leadership & management development
                  </h4>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    Burnout recovery & resilience
                  </h4>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    Employee engagement & retention
                  </h4>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    Culture transformation & accountability
                  </h4>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    Onboarding & professional growth initiatives
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* About Audrey */}
          <div className="bg-gradient-to-br from-slate-800/50 to-blue-900/50 backdrop-blur-md border border-white/20 rounded-2xl p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-black text-white mb-6">
                  AUDREY EGYPT YOUNG
                </h2>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  Founder of eBlackBookListings, The Movement Magazine, 
                  The Ultimate Author Social Media eCoSystem, and THE MINDSET SHIFT eCoSystem.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Informed by three decades of leadership experience within a public education system.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Corporate & Bulk Orders Available
                </h3>
                <ul className="space-y-3 text-gray-200 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Books – Full eCoSystem Bundles
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Licensing Options
                  </li>
                </ul>
                <a 
                  href="/"
                  className="text-green-400 hover:text-green-300 font-bold text-lg flex items-center gap-2"
                >
                  www.eBlackBookListings.com
                  <span>→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MindsetShiftEcosystemPage;
